import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

const fmtUSD = (n) =>
    `$${Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const LEVEL_STYLES = {
    pass: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        label: "Pass",
        Icon: CheckCircle2,
    },
    warn: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        label: "Warning",
        Icon: AlertTriangle,
    },
    fail: {
        bg: "bg-rose-50",
        border: "border-rose-200",
        text: "text-rose-700",
        label: "Fail",
        Icon: XCircle,
    },
};

function LevelBadge({ level }) {
    const s = LEVEL_STYLES[level] || LEVEL_STYLES.pass;
    const { Icon } = s;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded border ${s.bg} ${s.border} ${s.text}`}
            data-testid={`integrity-badge-${level}`}
        >
            <Icon className="h-3 w-3" />
            {s.label}
        </span>
    );
}

function CheckRow({ check }) {
    const [open, setOpen] = useState(false);
    const isMoneyCheck = check.master !== undefined && check.sum !== undefined;
    const hasFailures = (check.fail_count || 0) > 0;
    const hasWarnings = (check.warn_count || 0) > 0;
    const hasExamples = Array.isArray(check.examples) && check.examples.length > 0;
    const showDetails = hasFailures || hasWarnings || hasExamples || isMoneyCheck;

    return (
        <div
            className="border rounded-md asl-card p-3"
            data-testid={`integrity-check-row-${check.level}`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <LevelBadge level={check.level} />
                        <span className="text-sm font-semibold asl-navy-text">{check.name}</span>
                    </div>
                    {isMoneyCheck && (
                        <div className="text-xs text-[color:var(--asl-muted)] mt-1 mono">
                            sum {fmtUSD(check.sum)} · master {fmtUSD(check.master)} ·
                            delta {check.delta >= 0 ? "+" : ""}
                            {Number(check.delta).toFixed(2)}
                        </div>
                    )}
                    {!isMoneyCheck && (check.fail_count != null || check.warn_count != null) && (
                        <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                            {check.fail_count > 0 && (
                                <span className="text-rose-600 font-semibold">
                                    {check.fail_count} failing
                                </span>
                            )}
                            {check.fail_count > 0 && check.warn_count > 0 && " · "}
                            {check.warn_count > 0 && (
                                <span className="text-amber-600 font-semibold">
                                    {check.warn_count} warning
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {showDetails && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpen((o) => !o)}
                        data-testid="integrity-details-toggle"
                    >
                        {open ? (
                            <>
                                Hide details <ChevronUp className="h-3 w-3 ml-1" />
                            </>
                        ) : (
                            <>
                                View details <ChevronDown className="h-3 w-3 ml-1" />
                            </>
                        )}
                    </Button>
                )}
            </div>

            {open && (
                <div className="mt-3 text-xs space-y-2">
                    {isMoneyCheck && check.per_module && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                                Per module
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mono">
                                {Object.entries(check.per_module).map(([code, val]) => (
                                    <div key={code} className="flex justify-between border rounded px-2 py-1">
                                        <span className="text-[color:var(--asl-muted)]">{code}</span>
                                        <span className="font-semibold">
                                            {typeof val === "number" && Math.abs(val) > 0
                                                ? fmtUSD(val)
                                                : val}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {hasExamples && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                                Failing / warning rows
                            </div>
                            <ul className="space-y-1">
                                {check.examples.map((ex, i) => (
                                    <li key={i} className="border rounded px-2 py-1 mono">
                                        <span className="font-semibold">{ex.period_name || ex.period_id}</span>{" "}
                                        {ex.lines_sum != null && (
                                            <>
                                                — lines_sum {fmtUSD(ex.lines_sum)} ≠ period.net_payable_total{" "}
                                                {fmtUSD(ex.period_total)} (delta{" "}
                                                {ex.delta >= 0 ? "+" : ""}
                                                {Number(ex.delta).toFixed(2)})
                                            </>
                                        )}
                                        {ex.expected != null && (
                                            <>
                                                — funding {fmtUSD(ex.actual)} ≠ expected {fmtUSD(ex.expected)}{" "}
                                                (delta {ex.delta >= 0 ? "+" : ""}
                                                {Number(ex.delta).toFixed(2)})
                                            </>
                                        )}
                                        {ex.expected == null && ex.lines_sum == null && (
                                            <span> — missing funding record</span>
                                        )}{" "}
                                        <LevelBadge level={ex.level || "fail"} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function FinanceIntegrityPanel({ from, to }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const run = async () => {
        setLoading(true);
        try {
            const params = {};
            if (from) params.date_from = from;
            if (to) params.date_to = to;
            const { data: res } = await api.get("/finance/integrity-check", { params });
            setData(res);
            if (res.summary.overall === "fail") toast.error("Finance integrity: failures found.");
            else if (res.summary.overall === "warn") toast.warning("Finance integrity: warnings only.");
            else toast.success("Finance integrity: all checks pass.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const overall = data?.summary?.overall;

    return (
        <div className="asl-card p-4 space-y-4" data-testid="finance-integrity-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 asl-navy-text" />
                    <div>
                        <h3 className="text-base font-bold asl-navy-text">Finance Integrity Check</h3>
                        <p className="text-xs text-[color:var(--asl-muted)]">
                            Verifies that module-level finance data sums up correctly to the master roll-up.
                            Delta ≤ $0.05 is a rounding warning, &gt; $0.05 is a failure.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {overall && <LevelBadge level={overall} />}
                    <Button
                        variant="default"
                        size="sm"
                        onClick={run}
                        disabled={loading}
                        data-testid="integrity-run-btn"
                    >
                        {loading ? "Running…" : data ? "Re-run check" : "Run check"}
                    </Button>
                </div>
            </div>

            {data && (
                <>
                    <div
                        className="grid grid-cols-2 md:grid-cols-4 gap-3"
                        data-testid="integrity-summary"
                    >
                        <SummaryStat label="Total checks" value={data.summary.total_checks} />
                        <SummaryStat label="Passed" value={data.summary.passed} tone="pass" />
                        <SummaryStat label="Warnings" value={data.summary.warnings} tone="warn" />
                        <SummaryStat label="Failed" value={data.summary.failed} tone="fail" />
                    </div>
                    <div className="space-y-2" data-testid="integrity-checks-list">
                        {data.checks.map((c, i) => (
                            <CheckRow key={i} check={c} />
                        ))}
                    </div>
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        Generated <span className="mono">{data.generated_at}</span> by{" "}
                        <span className="mono">{data.generated_by}</span>
                    </div>
                </>
            )}
        </div>
    );
}

function SummaryStat({ label, value, tone }) {
    const cls =
        tone === "pass"
            ? "text-emerald-700"
            : tone === "warn"
                ? "text-amber-700"
                : tone === "fail"
                    ? "text-rose-700"
                    : "asl-navy-text";
    return (
        <div className="border rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-xl font-bold mono mt-1 ${cls}`}>{value}</div>
        </div>
    );
}
